/**
 * WalletVaultService - Core wallet management service
 */

import { Keypair, Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import bs58 from 'bs58';
import logger from '../../utils/logger';
import * as CryptoUtils from './cryptoUtils';
import {
  STORAGE_KEY,
  STORAGE_KEY_ENCRYPTED,
  STORAGE_KEY_SALT,
  STORAGE_KEY_AUTH_TYPE,
  VAULT_VERSION,
  AUTO_LOCK_MS,
  WALLET_CATEGORIES,
  WALLET_STATUS,
  AUTH_TYPES
} from './constants';

class WalletVaultService {
  constructor() {
    this.wallets = [];
    this.groups = [];
    this.settings = {
      autoBackup: true,
      trackBalances: true,
      defaultCategory: WALLET_CATEGORIES.BUNDLE,
      encryptionEnabled: false
    };
    this.loaded = false;
    this._locked = true;
    this._encryptionKey = null;
    this._autoLockTimer = null;
    this._lastActivity = Date.now();
    this._listeners = new Set();
  }

  // === SUBSCRIPTION & NOTIFICATION ===
  
  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  _notify() {
    this._listeners.forEach(listener => listener({
      locked: this._locked,
      loaded: this.loaded,
      walletCount: this.wallets.length
    }));
  }

  _resetAutoLock() {
    this._lastActivity = Date.now();
    if (this._autoLockTimer) {
      clearTimeout(this._autoLockTimer);
    }
    if (!this._locked && this.settings.encryptionEnabled) {
      this._autoLockTimer = setTimeout(() => {
        this.lock();
      }, AUTO_LOCK_MS);
    }
  }

  // === LOCK/UNLOCK ===
  
  isLocked() {
    return this._locked && this.settings.encryptionEnabled;
  }

  isEncryptionSetup() {
    return !!localStorage.getItem(STORAGE_KEY_SALT);
  }

  async setupEncryption(password) {
    if (!password || password.length < 8) {
      throw new Error('Password must be at least 8 characters');
    }

    const salt = CryptoUtils.generateSalt();
    const key = await CryptoUtils.deriveKey(password, salt);
    
    localStorage.setItem(STORAGE_KEY_SALT, btoa(String.fromCharCode(...salt)));
    
    this._encryptionKey = key;
    this.settings.encryptionEnabled = true;
    await this._saveEncrypted();
    
    localStorage.removeItem(STORAGE_KEY);
    
    this._locked = false;
    this._resetAutoLock();
    this._notify();
    
    return true;
  }

  async unlock(password) {
    if (!this.isEncryptionSetup()) {
      throw new Error('Encryption not set up');
    }

    const saltB64 = localStorage.getItem(STORAGE_KEY_SALT);
    const salt = Uint8Array.from(atob(saltB64), c => c.charCodeAt(0));
    const key = await CryptoUtils.deriveKey(password, salt);
    
    const encryptedData = localStorage.getItem(STORAGE_KEY_ENCRYPTED);
    if (!encryptedData) {
      throw new Error('No encrypted data found');
    }

    try {
      const data = await CryptoUtils.decrypt(encryptedData, key);
      this.wallets = data.wallets || [];
      this.groups = data.groups || [];
      this.settings = { ...this.settings, ...data.settings };
      this._encryptionKey = key;
      this._locked = false;
      this.loaded = true;
      this._resetAutoLock();
      this._notify();
      return true;
    } catch (e) {
      throw new Error('Invalid password');
    }
  }

  lock() {
    if (this.settings.encryptionEnabled) {
      this._encryptionKey = null;
      this._locked = true;
      if (this._autoLockTimer) {
        clearTimeout(this._autoLockTimer);
        this._autoLockTimer = null;
      }
      this._notify();
    }
  }

  async changePassword(oldPassword, newPassword) {
    await this.unlock(oldPassword);
    
    const salt = CryptoUtils.generateSalt();
    const key = await CryptoUtils.deriveKey(newPassword, salt);
    
    localStorage.setItem(STORAGE_KEY_SALT, btoa(String.fromCharCode(...salt)));
    this._encryptionKey = key;
    await this._saveEncrypted();
    
    this._resetAutoLock();
    return true;
  }

  async removeEncryption(password) {
    await this.unlock(password);
    
    localStorage.removeItem(STORAGE_KEY_SALT);
    localStorage.removeItem(STORAGE_KEY_ENCRYPTED);
    localStorage.removeItem(STORAGE_KEY_AUTH_TYPE);
    
    this.settings.encryptionEnabled = false;
    this._encryptionKey = null;
    this._locked = false;
    
    this.save();
    this._notify();
    return true;
  }

  // === WALLET SIGNATURE-BASED ENCRYPTION ===

  /**
   * Get the authentication type (password or signature)
   * @returns {'password' | 'signature' | null}
   */
  getAuthType() {
    return localStorage.getItem(STORAGE_KEY_AUTH_TYPE) || null;
  }

  /**
   * Check if vault uses signature-based encryption
   * @returns {boolean}
   */
  isSignatureAuth() {
    return this.getAuthType() === AUTH_TYPES.SIGNATURE;
  }

  /**
   * Setup encryption using wallet signature
   * @param {Uint8Array} signatureBytes - Signature from wallet.signMessage()
   * @returns {Promise<boolean>}
   */
  async setupSignatureEncryption(signatureBytes) {
    if (!signatureBytes || signatureBytes.length < 64) {
      throw new Error('Invalid signature');
    }

    const key = await CryptoUtils.deriveKeyFromSignature(signatureBytes);
    
    // Mark as signature-based auth (no salt needed - it's fixed)
    localStorage.setItem(STORAGE_KEY_AUTH_TYPE, AUTH_TYPES.SIGNATURE);
    
    this._encryptionKey = key;
    this.settings.encryptionEnabled = true;
    await this._saveEncrypted();
    
    // Remove unencrypted data
    localStorage.removeItem(STORAGE_KEY);
    
    this._locked = false;
    this._resetAutoLock();
    this._notify();
    
    return true;
  }

  /**
   * Unlock vault using wallet signature
   * @param {Uint8Array} signatureBytes - Signature from wallet.signMessage()
   * @returns {Promise<boolean>}
   */
  async unlockWithSignature(signatureBytes) {
    if (!this.isEncryptionSetup()) {
      throw new Error('Encryption not set up');
    }

    if (!this.isSignatureAuth()) {
      throw new Error('Vault uses password authentication, not signature');
    }

    const key = await CryptoUtils.deriveKeyFromSignature(signatureBytes);
    
    const encryptedData = localStorage.getItem(STORAGE_KEY_ENCRYPTED);
    if (!encryptedData) {
      throw new Error('No encrypted data found');
    }

    try {
      const data = await CryptoUtils.decrypt(encryptedData, key);
      this.wallets = data.wallets || [];
      this.groups = data.groups || [];
      this.settings = { ...this.settings, ...data.settings };
      this._encryptionKey = key;
      this._locked = false;
      this.loaded = true;
      this._resetAutoLock();
      this._notify();
      return true;
    } catch (e) {
      throw new Error('Invalid signature - cannot decrypt vault');
    }
  }

  /**
   * Check if vault has any data (encrypted or not)
   * @returns {boolean}
   */
  hasData() {
    return !!(localStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY_ENCRYPTED));
  }

  // === STORAGE ===
  
  async _saveEncrypted() {
    if (!this._encryptionKey) {
      throw new Error('Vault is locked');
    }

    const data = {
      version: VAULT_VERSION,
      updatedAt: Date.now(),
      wallets: this.wallets,
      groups: this.groups,
      settings: this.settings
    };

    const encrypted = await CryptoUtils.encrypt(data, this._encryptionKey);
    localStorage.setItem(STORAGE_KEY_ENCRYPTED, encrypted);
    return true;
  }

  init() {
    if (this.loaded) return this;
    
    if (this.isEncryptionSetup()) {
      this.settings.encryptionEnabled = true;
      this._locked = true;
      this.loaded = false;
      this._notify();
      return this;
    }
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        this.wallets = data.wallets || [];
        this.groups = data.groups || [];
        this.settings = { ...this.settings, ...data.settings };
      }
      this._locked = false;
      this.loaded = true;
    } catch (e) {
      logger.error('Failed to load wallet vault', { error: e.message });
      this.wallets = [];
      this.groups = [];
    }
    
    this._notify();
    return this;
  }

  save() {
    this._resetAutoLock();
    
    if (this.settings.encryptionEnabled && this._encryptionKey) {
      return this._saveEncrypted();
    }
    
    try {
      const data = {
        version: VAULT_VERSION,
        updatedAt: Date.now(),
        wallets: this.wallets,
        groups: this.groups,
        settings: this.settings
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      return true;
    } catch (e) {
      logger.error('Failed to save wallet vault', { error: e.message });
      return false;
    }
  }

  // === WALLET OPERATIONS ===
  
  generateWallet(options = {}) {
    const {
      label = '',
      category = WALLET_CATEGORIES.BUNDLE,
      groupId = null,
      buyAmount = 0,
      metadata = {}
    } = options;

    const keypair = Keypair.generate();
    const wallet = {
      id: `wallet_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      publicKey: keypair.publicKey.toBase58(),
      privateKey: bs58.encode(keypair.secretKey),
      label: label || `Wallet ${this.wallets.length + 1}`,
      category,
      status: WALLET_STATUS.ACTIVE,
      groupId,
      buyAmount,
      balance: 0,
      createdAt: Date.now(),
      lastActivity: null,
      transactions: 0,
      metadata,
      tags: []
    };

    this.wallets.push(wallet);
    this.save();
    
    return wallet;
  }

  generateBatch(count, options = {}) {
    const {
      category = WALLET_CATEGORIES.BUNDLE,
      groupId = null,
      buyRange = { min: 0.005, max: 0.02 },
      labelPrefix = 'Wallet'
    } = options;

    const newWallets = [];
    
    for (let i = 0; i < count; i++) {
      const buyAmount = buyRange.min + Math.random() * (buyRange.max - buyRange.min);
      const wallet = this.generateWallet({
        label: `${labelPrefix} ${this.wallets.length + 1}`,
        category,
        groupId,
        buyAmount: Math.round(buyAmount * 10000) / 10000
      });
      newWallets.push(wallet);
    }

    return newWallets;
  }

  importWallet(privateKey, options = {}) {
    try {
      const secretKey = bs58.decode(privateKey.trim());
      const keypair = Keypair.fromSecretKey(secretKey);
      const publicKey = keypair.publicKey.toBase58();

      if (this.wallets.some(w => w.publicKey === publicKey)) {
        throw new Error('Wallet already exists in vault');
      }

      const wallet = {
        id: `wallet_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        publicKey,
        privateKey: privateKey.trim(),
        label: options.label || `Imported ${this.wallets.length + 1}`,
        category: options.category || WALLET_CATEGORIES.BUNDLE,
        status: WALLET_STATUS.ACTIVE,
        groupId: options.groupId || null,
        buyAmount: options.buyAmount || 0,
        balance: 0,
        createdAt: Date.now(),
        importedAt: Date.now(),
        lastActivity: null,
        transactions: 0,
        metadata: options.metadata || {},
        tags: options.tags || []
      };

      this.wallets.push(wallet);
      this.save();
      
      return wallet;
    } catch (e) {
      throw new Error(`Invalid private key: ${e.message}`);
    }
  }

  importBatch(walletsData) {
    const imported = [];
    const errors = [];

    walletsData.forEach((w, i) => {
      try {
        const wallet = this.importWallet(w.privateKey, {
          label: w.label,
          category: w.category,
          buyAmount: w.buyAmount,
          tags: w.tags
        });
        imported.push(wallet);
      } catch (e) {
        errors.push({ index: i, error: e.message });
      }
    });

    return { imported, errors };
  }

  // === GETTERS ===
  
  getAll() { return [...this.wallets]; }
  getByCategory(category) { return this.wallets.filter(w => w.category === category); }
  getByGroup(groupId) { return this.wallets.filter(w => w.groupId === groupId); }
  getByStatus(status) { return this.wallets.filter(w => w.status === status); }
  getById(id) { return this.wallets.find(w => w.id === id); }
  getByPublicKey(publicKey) { return this.wallets.find(w => w.publicKey === publicKey); }

  // === UPDATES ===
  
  updateWallet(id, updates) {
    const index = this.wallets.findIndex(w => w.id === id);
    if (index === -1) return null;

    const { privateKey, publicKey, id: _, ...safeUpdates } = updates;
    
    this.wallets[index] = {
      ...this.wallets[index],
      ...safeUpdates,
      updatedAt: Date.now()
    };
    
    this.save();
    return this.wallets[index];
  }

  updateBatch(ids, updates) {
    ids.forEach(id => this.updateWallet(id, updates));
    return this.wallets.filter(w => ids.includes(w.id));
  }

  deleteWallet(id) {
    const index = this.wallets.findIndex(w => w.id === id);
    if (index === -1) return false;
    this.wallets.splice(index, 1);
    this.save();
    return true;
  }

  deleteBatch(ids) {
    this.wallets = this.wallets.filter(w => !ids.includes(w.id));
    this.save();
    return true;
  }

  archiveWallet(id) {
    return this.updateWallet(id, { status: WALLET_STATUS.ARCHIVED });
  }

  // === GROUPS ===
  
  createGroup(name, options = {}) {
    const group = {
      id: `group_${Date.now()}`,
      name,
      description: options.description || '',
      category: options.category || WALLET_CATEGORIES.BUNDLE,
      createdAt: Date.now(),
      metadata: options.metadata || {}
    };

    this.groups.push(group);
    this.save();
    return group;
  }

  getGroups() { return [...this.groups]; }

  deleteGroup(groupId, deleteWallets = false) {
    if (deleteWallets) {
      this.wallets = this.wallets.filter(w => w.groupId !== groupId);
    } else {
      this.wallets.forEach(w => {
        if (w.groupId === groupId) w.groupId = null;
      });
    }
    
    this.groups = this.groups.filter(g => g.id !== groupId);
    this.save();
    return true;
  }

  // === BALANCE CHECKING ===
  
  async checkBalances(walletIds, rpcUrl) {
    const connection = new Connection(rpcUrl);
    const results = {};

    for (const id of walletIds) {
      const wallet = this.getById(id);
      if (!wallet) continue;

      try {
        const pubkey = new PublicKey(wallet.publicKey);
        const balance = await connection.getBalance(pubkey);
        const solBalance = balance / LAMPORTS_PER_SOL;
        
        this.updateWallet(id, {
          balance: solBalance,
          lastBalanceCheck: Date.now()
        });
        
        results[id] = { success: true, balance: solBalance };
      } catch (e) {
        results[id] = { success: false, error: e.message };
      }
    }

    return results;
  }

  async checkAllBalances(rpcUrl) {
    const ids = this.wallets.map(w => w.id);
    return this.checkBalances(ids, rpcUrl);
  }

  getTotalBalance() {
    return this.wallets.reduce((sum, w) => sum + (w.balance || 0), 0);
  }

  getTotalBuyAmount(category = null) {
    const wallets = category ? this.getByCategory(category) : this.wallets;
    return wallets.reduce((sum, w) => sum + (w.buyAmount || 0), 0);
  }

  // === EXPORT ===
  
  exportWallets(walletIds = null, includePrivateKeys = true) {
    const walletsToExport = walletIds
      ? this.wallets.filter(w => walletIds.includes(w.id))
      : this.wallets;

    return {
      version: VAULT_VERSION,
      exportedAt: new Date().toISOString(),
      totalWallets: walletsToExport.length,
      wallets: walletsToExport.map(w => ({
        publicKey: w.publicKey,
        privateKey: includePrivateKeys ? w.privateKey : undefined,
        label: w.label,
        category: w.category,
        status: w.status,
        buyAmount: w.buyAmount,
        balance: w.balance,
        createdAt: w.createdAt,
        tags: w.tags
      }))
    };
  }

  downloadExport(walletIds = null, filename = null) {
    const data = this.exportWallets(walletIds, true);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `wallet-vault-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // === STATS & UTILITIES ===
  
  getStats() {
    const byCategory = {};
    const byStatus = {};

    this.wallets.forEach(w => {
      byCategory[w.category] = (byCategory[w.category] || 0) + 1;
      byStatus[w.status] = (byStatus[w.status] || 0) + 1;
    });

    return {
      totalWallets: this.wallets.length,
      totalGroups: this.groups.length,
      totalBalance: this.getTotalBalance(),
      totalBuyAmount: this.getTotalBuyAmount(),
      byCategory,
      byStatus,
      activeWallets: byStatus[WALLET_STATUS.ACTIVE] || 0,
      readyWallets: byStatus[WALLET_STATUS.READY] || 0
    };
  }

  clearAll() {
    this.wallets = [];
    this.groups = [];
    localStorage.removeItem(STORAGE_KEY);
    return true;
  }

  getKeypair(id) {
    const wallet = this.getById(id);
    if (!wallet) return null;
    
    try {
      const secretKey = bs58.decode(wallet.privateKey);
      return Keypair.fromSecretKey(secretKey);
    } catch {
      return null;
    }
  }

  randomizeBuyAmounts(walletIds, range = { min: 0.005, max: 0.02 }) {
    walletIds.forEach(id => {
      const amount = range.min + Math.random() * (range.max - range.min);
      this.updateWallet(id, {
        buyAmount: Math.round(amount * 10000) / 10000
      });
    });
    return this.wallets.filter(w => walletIds.includes(w.id));
  }

  getWalletAge(id) {
    const wallet = this.getById(id);
    if (!wallet) return 0;
    return (Date.now() - wallet.createdAt) / (24 * 60 * 60 * 1000);
  }

  getAgedWallets(minAgeDays = 7) {
    return this.wallets.filter(w => {
      const age = this.getWalletAge(w.id);
      return age >= minAgeDays && w.status !== WALLET_STATUS.ARCHIVED;
    });
  }
}

export default WalletVaultService;
